// The outstanding-by-age bar: one horizontal run across the KPI block, under
// the four cards, grouping the invoices still in progress by how long ago they
// arrived (owner, 2026-09-15: "a horizontal visual that stretches across all
// four boxes above it that looks like a progress bar and uses different colors
// to group outstanding invoices by age").
//
// IT DECOMPOSES *INVOICES IN PROGRESS*, which is why it sits under the cards
// rather than in a panel of its own: its segments sum to that card exactly.
//
// IT DOES NOT PARTITION *INVOICES PAST DUE*, and the near-miss is worth naming
// because it looks like a bug the first time it is noticed. Both are aged from
// `uploaded_at`, but the card compares TIMESTAMPS (uploaded more than seven
// days ago) while a band compares WHOLE DAYS (0-7, 8-14, …), so an invoice
// seven and a half days old is past due and still in the green band. The
// past-due count can therefore exceed the sum of the bands past the threshold
// by the handful sitting in that half-day. Making them agree would cost either
// the card's plain meaning or the bands' whole-day bounds, and neither is
// worth buying.
//
// THE BANDS ARE THE SERVER'S. Each arrives with its own label and day bounds
// (`OUTSTANDING_AGE_BANDS`), and nothing here hardcodes a threshold — the colour
// is chosen by POSITION, youngest through oldest, so the scale can be re-cut
// server-side without touching this file. It was re-cut once already, on
// 2026-09-20, twice: from four ranges running to 31+ down to one band per day
// and a 7+ tail (owner: "the scale should be from 0 to 7+ days"), and then to
// the four bands it has now, because eight bands over a four-colour ramp put
// two bands on each hue (owner: "instead of doing 2 days and 3 days in the same
// color, just do 2 - 3 days as one color. Reserve 7+ days to be its own
// color"). Neither re-cut touched this file, which is the point of colouring by
// position — but note the CAP that second one implies: more bands than
// `AGE_RAMP` has colours silently doubles up again.
//
// "AGE" SITS AT THE LEFT END rather than in a sentence underneath (owner,
// 2026-09-20), in the `Caption1` the four KPI cards title themselves with. A
// trailing "outstanding by age; past due at 7 days" stood there for five days
// and went with the same instruction: the legend already names every band in
// days, so the sentence restated the axis and then added a threshold nobody had
// asked it to carry.
//
// A BAND AT ZERO DRAWS NOTHING BUT KEEPS ITS LEGEND ENTRY. Dropping it from the
// legend would make the legend change shape as work moves through, and a legend
// that moves is one a reader has to re-read.
import { Caption1, makeStyles, tokens } from '@fluentui/react-components'
import type { AgeBandTile, AnalyticsKpiTile } from '../api/types'
import { ageRampFill } from '../lib/theme'
import { formatCurrency } from '../lib/format'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '8px' },
  // The title sits at the LEFT END, on the track's own line (owner,
  // 2026-09-20). It read "Age" until D-450 (owner, 2026-11-02): "'Age' does
  // not mean anything to me. ... let's call this what it is: 'Days Since
  // Uploaded Date'". The bands age from `uploaded_at`.
  // `alignItems: center` so the word centres against a 12px bar rather than
  // sitting on its top edge.
  row: { display: 'flex', alignItems: 'center', gap: '10px' },
  // THE SAME FONT AS THE OTHER VISUALS' TITLES IN THIS BOX (owner, 2026-09-20).
  // The four KPI cards title themselves with a `Caption1` in
  // `colorNeutralForeground3` (`DashboardCharts.tsx`'s `kpiLabel`), so this is
  // that, not a `Subtitle2` — the section headings outside the box are the
  // larger type, and matching those would make the bar look like a sixth
  // section rather than a fifth visual inside this one.
  title: { color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },
  track: {
    display: 'flex',
    width: '100%',
    height: '12px',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  segment: { height: '100%' },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px',
    color: tokens.colorNeutralForeground3,
  },
  entry: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  swatch: { width: '10px', height: '10px', borderRadius: '2px', flexShrink: 0 },
  empty: { color: tokens.colorNeutralForeground3 },
})

/**
 * "2 days", "7+ days", "8-14 days" — the band's own bounds, never a hardcoded
 * one.
 *
 * Three shapes because a band can be any of them. The KPI bar's four are two
 * ranges, a third range and an open top. The
 * single-day form earned its place on 2026-09-20, when the bar briefly ran one
 * band per day and "2-2 days" would have been absurd — it is kept because a
 * one-day band is a re-cut away and the formatter should not have to change
 * with the scale.
 */
function bandLabel(band: AgeBandTile): string {
  if (band.max_days === null) return `${band.min_days}+ days`
  if (band.max_days === band.min_days) {
    return band.min_days === 1 ? '1 day' : `${band.min_days} days`
  }
  return `${band.min_days}-${band.max_days} days`
}

interface Props {
  bands: readonly AgeBandTile[]
  /** In progress with nothing to age from. Draws a neutral tail segment only
      when it is non-zero, which is practically never — but an invoice that
      vanished from the bar while counting in the card above it would be a
      silent disagreement, and this is what stops one. */
  unknown: AnalyticsKpiTile
}

export function AgeProgressBar({ bands, unknown }: Props) {
  const s = useStyles()
  const total = bands.reduce((sum, band) => sum + band.count, 0) + unknown.count
  if (total === 0) {
    return <Caption1 className={s.empty}>Nothing is outstanding.</Caption1>
  }
  return (
    <div className={s.root}>
      <div className={s.row}>
        <Caption1 className={s.title}>Days Since Uploaded Date</Caption1>
        <div
          className={s.track}
        role="img"
        aria-label={`Outstanding invoices by age: ${bands
          .map((band) => `${band.count} at ${bandLabel(band)}`)
          .join(', ')}`}
      >
          {bands.map((band, index) =>
            band.count === 0 ? null : (
              <div
                key={band.label}
                className={s.segment}
                style={{
                  // `flexGrow` rather than a percentage width: the segments then
                  // divide the track exactly, with no rounding gap at the end.
                  flexGrow: band.count,
                  backgroundColor: ageRampFill(index, bands.length),
                }}
                title={`${band.count} invoice${band.count === 1 ? '' : 's'} at ${bandLabel(band)} — ${formatCurrency(band.total)}`}
              />
            ),
          )}
          {unknown.count > 0 && (
            <div
              className={s.segment}
              style={{ flexGrow: unknown.count, backgroundColor: tokens.colorNeutralStroke1 }}
              title={`${unknown.count} with no uploaded date to age from`}
            />
          )}
        </div>
      </div>
      <div className={s.legend}>
        {bands.map((band, index) => (
          <span key={band.label} className={s.entry}>
            <span
              className={s.swatch}
              style={{ backgroundColor: ageRampFill(index, bands.length) }}
              aria-hidden
            />
            <Caption1>
              {bandLabel(band)}: {band.count}
            </Caption1>
          </span>
        ))}
        {unknown.count > 0 && (
          <span className={s.entry}>
            <span
              className={s.swatch}
              style={{ backgroundColor: tokens.colorNeutralStroke1 }}
              aria-hidden
            />
            <Caption1>No uploaded date: {unknown.count}</Caption1>
          </span>
        )}
      </div>
    </div>
  )
}
